import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { imageService } from '@/services/image';
import { useToast } from '@/components/common/Toast';
import { useSellerStore } from '@/store/seller';
import { supabase } from '@/lib/supabase';

interface StoreInfo {
  name: string;
  image_url: string;
  bestmenu1: string;
  bestmenu2: string;
  bestmenu3: string;
  address: string;
  phone: string;
  notice: string;
  description: string;
}

interface BusinessInfo {
  business_name: string;
  representative_name: string;
  business_number: string;
}

interface AccountInfo {
  settlement_bank_name: string;
  settlement_account_number: string;
  settlement_account_holder: string;
}

export function DesktopStoreEditPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { setLoading, setError } = useSellerStore();

  const [isEditMode, setIsEditMode] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [sellerId, setSellerId] = useState<string>('');
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    name: '',
    image_url: '',
    bestmenu1: '',
    bestmenu2: '',
    bestmenu3: '',
    address: '',
    phone: '',
    notice: '',
    description: ''
  });
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    business_name: '',
    representative_name: '',
    business_number: ''
  });
  const [accountInfo, setAccountInfo] = useState<AccountInfo>({
    settlement_bank_name: '',
    settlement_account_number: '',
    settlement_account_holder: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);

  useEffect(() => {
    loadStoreData();
  }, []);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      
      // 1. 레스토랑 ID 가져오기
      const restaurantResult = await sellerService.getSellerRestaurantId();
      if (!restaurantResult.success || !restaurantResult.restaurantId) {
        setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
        return;
      }

      setRestaurantId(restaurantResult.restaurantId);

      // 2. 판매자 ID 가져오기 (auth.uid)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setSellerId(user.id);
      }

      // 3. 가게 정보 가져오기
      const storeResult = await sellerService.getStoreInfo(restaurantResult.restaurantId);
      if (storeResult.success && storeResult.data) {
        const { restaurant, business } = storeResult.data;
        
        setStoreInfo({
          name: restaurant.name || '',
          image_url: restaurant.image_url || '',
          bestmenu1: restaurant.bestmenu1 || '',
          bestmenu2: restaurant.bestmenu2 || '',
          bestmenu3: restaurant.bestmenu3 || '',
          address: restaurant.address || '',
          phone: restaurant.phone || '',
          notice: restaurant.notice || '',
          description: restaurant.description || ''
        });

        setBusinessInfo({
          business_name: business.business_name || '',
          representative_name: business.representative_name || '',
          business_number: business.business_number || ''
        });

        setAccountInfo({
          settlement_bank_name: restaurant.settlement_bank_name || '',
          settlement_account_number: restaurant.settlement_account_number || '',
          settlement_account_holder: restaurant.settlement_account_holder || ''
        });
      }
      
    } catch (error) {
      console.error('Store data load error:', error);
      setError('가게 정보를 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof StoreInfo, value: string) => {
    setStoreInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !restaurantId || !sellerId) return;

    try {
      setIsUploading(true);
      
      const result = await imageService.uploadRestaurantImage(
        file,
        restaurantId
      );

      if (result.success && result.url) {
        setStoreInfo(prev => ({
          ...prev,
          image_url: result.url!
        }));
        showToast('이미지가 성공적으로 업로드되었습니다');
      } else {
        showToast(result.error || '이미지 업로드에 실패했습니다');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      showToast('이미지 업로드 중 오류가 발생했습니다');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) return;

    try {
      setLoading(true);

      const result = await sellerService.updateStoreInfo(restaurantId, storeInfo);
      
      if (result.success) {
        showToast('가게 정보가 성공적으로 저장되었습니다');
        setIsEditMode(false);
      } else {
        showToast(result.error || '가게 정보 저장에 실패했습니다');
      }
    } catch (error) {
      console.error('Store info save error:', error);
      showToast('가게 정보 저장 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const AccountInfoModal = () => (
    showAccountModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md mx-4">
          <h3 className="text-[24px] font-bold mb-4">정산 계좌 정보 수정</h3>
          <p className="text-[16px] text-gray-600 mb-6">
            정산 계좌 정보는 중요한 내용이므로 아래 연락처로 직접 문의해주세요.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-[16px] font-semibold text-gray-700">이메일 문의</p>
            <p className="text-[16px] text-blue-600">ecodeal_cs@ecodeal.kr</p>
            <p className="text-[16px] font-semibold text-gray-700 mt-3">전화 문의</p>
            <p className="text-[16px] text-blue-600">관리자 문의 (평일 09:00-18:00)</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setShowAccountModal(false)}
              className="px-6 py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    )
  );

  const BusinessInfoModal = () => (
    showBusinessModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md mx-4">
          <h3 className="text-[24px] font-bold mb-4">사업자 정보 수정</h3>
          <p className="text-[16px] text-gray-600 mb-6">
            사업자 정보는 중요한 내용이므로 아래 연락처로 직접 문의해주세요.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-[16px] font-semibold text-gray-700">이메일 문의</p>
            <p className="text-[16px] text-blue-600">ecodeal_cs@ecodeal.kr</p>
            <p className="text-[16px] font-semibold text-gray-700 mt-3">전화 문의</p>
            <p className="text-[16px] text-blue-600">관리자 문의 (평일 09:00-18:00)</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setShowBusinessModal(false)}
              className="px-6 py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    )
  );

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-800 mr-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">기초정보</h1>
          </div>
          <button
            onClick={() => isEditMode ? handleSave() : setIsEditMode(true)}
            className="px-8 py-3 bg-green-500 text-white text-[18px] font-semibold rounded-xl hover:bg-green-600 transition-colors"
          >
            {isEditMode ? '저장하기' : '수정하기'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Store Information */}
          <div className="space-y-8">
            {/* 가게 정보 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <h2 className="text-[24px] font-semibold mb-6">가게 정보</h2>
              
              {/* 가게명 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">가게명</label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={storeInfo.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="가게명을 입력하세요"
                  />
                ) : (
                  <p className="text-[18px] text-gray-800">{storeInfo.name || '가게명이 설정되지 않았습니다'}</p>
                )}
              </div>

              {/* 배경사진 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">배경사진</label>
                {storeInfo.image_url && (
                  <img 
                    src={storeInfo.image_url} 
                    alt="가게 이미지" 
                    className="w-full h-48 object-cover rounded-lg mb-3" 
                  />
                )}
                {isEditMode && (
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="block w-full text-[16px] text-gray-500 file:mr-4 file:py-3 file:px-4 file:rounded-lg file:border-0 file:text-[16px] file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                    <p className="text-[14px] text-gray-500">
                      JPG, PNG, WebP 지원 • 최대 20MB • 업로드 시 자동 리사이징 • 새 이미지 업로드 시 기존 이미지는 교체됩니다
                    </p>
                    {isUploading && <p className="text-[14px] text-gray-500">이미지 업로드 중...</p>}
                  </div>
                )}
              </div>

              {/* 대표메뉴 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">대표메뉴</label>
                {isEditMode ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={storeInfo.bestmenu1}
                      onChange={(e) => handleInputChange('bestmenu1', e.target.value)}
                      className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="대표메뉴 1"
                    />
                    <input
                      type="text"
                      value={storeInfo.bestmenu2}
                      onChange={(e) => handleInputChange('bestmenu2', e.target.value)}
                      className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="대표메뉴 2"
                    />
                    <input
                      type="text"
                      value={storeInfo.bestmenu3}
                      onChange={(e) => handleInputChange('bestmenu3', e.target.value)}
                      className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="대표메뉴 3"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[18px] text-gray-800">{storeInfo.bestmenu1 || '대표메뉴 1이 설정되지 않았습니다'}</p>
                    <p className="text-[18px] text-gray-800">{storeInfo.bestmenu2 || '대표메뉴 2가 설정되지 않았습니다'}</p>
                    <p className="text-[18px] text-gray-800">{storeInfo.bestmenu3 || '대표메뉴 3이 설정되지 않았습니다'}</p>
                  </div>
                )}
              </div>

              {/* 가게 주소 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">가게 주소</label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={storeInfo.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="가게 주소를 입력하세요"
                  />
                ) : (
                  <p className="text-[18px] text-gray-800">{storeInfo.address || '주소가 설정되지 않았습니다'}</p>
                )}
              </div>

              {/* 가게 전화번호 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">가게 전화번호</label>
                {isEditMode ? (
                  <input
                    type="tel"
                    value={storeInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="전화번호를 입력하세요"
                  />
                ) : (
                  <p className="text-[18px] text-gray-800">{storeInfo.phone || '전화번호가 설정되지 않았습니다'}</p>
                )}
              </div>

              {/* 가게 공지 */}
              <div className="mb-6">
                <label className="block text-[16px] font-medium mb-2">가게 공지 (최대 200자)</label>
                {isEditMode ? (
                  <textarea
                    value={storeInfo.notice}
                    onChange={(e) => handleInputChange('notice', e.target.value.slice(0, 200))}
                    className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 h-32 resize-none"
                    placeholder="가게 공지사항을 입력하세요 (선택사항)"
                  />
                ) : (
                  <p className="text-[18px] text-gray-800 whitespace-pre-wrap">{storeInfo.notice || '공지사항이 없습니다'}</p>
                )}
                {isEditMode && (
                  <p className="text-[14px] text-gray-500 mt-2">{storeInfo.notice.length}/200자</p>
                )}
              </div>

              {/* 가게 소개 */}
              <div>
                <label className="block text-[16px] font-medium mb-2">가게 소개 (최대 150자)</label>
                {isEditMode ? (
                  <textarea
                    value={storeInfo.description}
                    onChange={(e) => handleInputChange('description', e.target.value.slice(0, 150))}
                    className="w-full px-4 py-3 text-[16px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 h-24 resize-none"
                    placeholder="가게 소개를 입력하세요 (선택사항)"
                  />
                ) : (
                  <p className="text-[18px] text-gray-800 whitespace-pre-wrap">{storeInfo.description || '가게 소개가 없습니다'}</p>
                )}
                {isEditMode && (
                  <p className="text-[14px] text-gray-500 mt-2">{storeInfo.description.length}/150자</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Business & Account Information */}
          <div className="space-y-8">
            {/* 가게 계좌 정보 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[24px] font-semibold">정산 계좌 정보</h2>
                <button
                  onClick={() => setShowAccountModal(true)}
                  className="px-4 py-2 text-[14px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  수정 문의
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-[16px] font-medium mb-2">은행명</label>
                  <p className="text-[18px] text-gray-800">{accountInfo.settlement_bank_name || '설정되지 않음'}</p>
                </div>
                <div>
                  <label className="block text-[16px] font-medium mb-2">계좌번호</label>
                  <p className="text-[18px] text-gray-800">{accountInfo.settlement_account_number || '설정되지 않음'}</p>
                </div>
                <div>
                  <label className="block text-[16px] font-medium mb-2">예금주명</label>
                  <p className="text-[18px] text-gray-800">{accountInfo.settlement_account_holder || '설정되지 않음'}</p>
                </div>
              </div>
            </div>

            {/* 사업자 정보 */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[24px] font-semibold">사업자 정보</h2>
                <button
                  onClick={() => setShowBusinessModal(true)}
                  className="px-4 py-2 text-[14px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  수정 문의
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-[16px] font-medium mb-2">상호명</label>
                  <p className="text-[18px] text-gray-800">{businessInfo.business_name}</p>
                </div>
                <div>
                  <label className="block text-[16px] font-medium mb-2">대표자명</label>
                  <p className="text-[18px] text-gray-800">{businessInfo.representative_name}</p>
                </div>
                <div>
                  <label className="block text-[16px] font-medium mb-2">사업자등록번호</label>
                  <p className="text-[18px] text-gray-800">{businessInfo.business_number}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AccountInfoModal />
      <BusinessInfoModal />
      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
